
import { Z_INDEX } from '@/lib/constants/z-index'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Award,
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  ClipboardList,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  Heart,
  Home,
  MessageCircle,
  MessageSquare,
  Palette,
  Plane,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  UsersRound,
  X
} from 'lucide-react'
import React from 'react'
import { User } from '../types/auth'
import UserInfo from './UserInfo'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ size?: string | number; className?: string }>
  roles: string[]
  comingSoon?: boolean // Indicates if this feature is under construction
}

interface NavSection {
  title: string
  items: NavItem[]
  roles: string[]
}

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  onJanesChatbotOpen: () => void
}

// Organized by user journey and information architecture
const navigationSections: NavSection[] = [
  {
    title: 'Início',
    roles: ['admin', 'director', 'diretor-tecnico', 'ceo' ],
    items: [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: Home,
        roles: ['admin', 'director', 'diretor-tecnico', 'ceo']
      }
    ]
  },
  {
    title: 'Resultados',
    roles: ['admin', 'director', 'diretor-tecnico'],
    items: [
      {
        name: 'Performance PROA',
        href: '/results',
        icon: Target,
        roles: ['admin', 'director', 'diretor-tecnico']
      },
      {
        name: 'Minhas Metas',
        href: '/my-business-targets',
        icon: TrendingUp,
        roles: ['admin', 'director', 'diretor-tecnico']
      }
    ],
  },
  {
    title: 'Desempenho',
    roles: ['admin', 'diretor-tecnico'],
    items: [
      {
        name: 'Resumo Geral',
        href: '/performance',
        icon: BarChart3,
        roles: ['admin', 'diretor-tecnico', 'director']
      },
      {
        name: 'Feedbacks',
        href: '/performance/feedbacks',
        icon: MessageSquare,
        roles: ['admin', 'diretor-tecnico', 'director']
      },
      {
        name: 'Meus Feedbacks',
        href: '/performance/feedbacks/received',
        icon: MessageSquare,
        roles: ['admin', 'diretor-tecnico', 'director', 'employee', 'ceo']
      },
      {
        name: 'Avaliação de Desempenho',
        href: '/performance/avaliacoes',
        icon: ClipboardList,
        roles: ['admin', 'diretor-tecnico', 'director']
      },
      {
        name: 'Avaliação Individual',
        href: '/performance/individual',
        icon: UserCheck,
        roles: ['admin', 'diretor-tecnico', 'director']
      },
      {
        name: 'Pesquisa de Clima',
        href: '/performance/clima',
        icon: Heart,
        roles: ['admin', 'diretor-tecnico', 'director']
      }
    ]
  },
  {
    title: 'Equipe',
    roles: ['admin', 'diretor-tecnico'],
    items: [
      {
        name: 'Colaboradores',
        href: '/employees',
        icon: Users,
        roles: ['admin', 'diretor-tecnico']
      },
      {
        name: 'Minha Equipe',
        href: '/my-team',
        icon: UsersRound,
        roles: ['admin', 'diretor-tecnico']
      },
      {
        name: 'Férias',
        href: '/vacation',
        icon: Plane,
        roles: ['admin', 'diretor-tecnico']
      },
      {
        name: 'Calendário',
        href: '/calendar',
        icon: Calendar,
        roles: ['admin', 'employee', 'diretor-tecnico']
      },
      {
        name: 'Onboarding',
        href: '/onboarding',
        icon: BookOpen,
        roles: ['admin']
      }
    ]
  },
  {
    title: 'Capacitação',
    roles: ['admin', 'employee', 'diretor-tecnico'],
    items: [
      // {
      //   name: 'Verba disponível',
      //   href: '/available-budget',
      //   icon: Wallet,
      //   roles: ['admin', 'employee', 'diretor-tecnico']
      // },
      {
        name: 'Meus cursos',
        href: '/my-courses',
        icon: Award,
        roles: ['admin', 'employee', 'diretor-tecnico']
      },
      {
        name: 'Cursos da Equipe',
        href: '/team-courses',
        icon: Award,
        roles: ['admin', 'diretor-tecnico']
      }
    ]
  },
  {
    title: 'Gestão de Pessoas',
    roles: ['admin'],
    items: [
      // {
      //   name: 'Dashboard',
      //   href: '/workforce',
      //   icon: Users,
      //   roles: ['admin']
      // },
      {
        name: 'Colaboradores',
        href: '/workforce/employees',
        icon: Users,
        roles: ['admin']
      },
      {
        name: 'Departamentos',
        href: '/workforce/departments',
        icon: Building2,
        roles: ['admin']
      },
      {
        name: 'Cargos',
        href: '/workforce/roles',
        icon: Briefcase,
        roles: ['admin']
      },
      {
        name: 'Folha de Pagamento',
        href: '/workforce/payroll',
        icon: DollarSign,
        roles: ['admin']
      }
    ]
  },
  // {
  //   title: 'Tempo & Benefícios',
  //   roles: ['admin', 'employee'],
  //   items: [
  //     {
  //       name: 'Férias',
  //       href: '/vacation',
  //       icon: Calendar,
  //       roles: ['admin', 'employee']
  //     }
  //   ]
  // },
  // {
  //   title: 'Desenvolvimento',
  //   roles: ['admin', 'employee'],
  //   items: [
  //     {
  //       name: 'Treinamentos',
  //       href: '/training',
  //       icon: Award,
  //       roles: ['admin', 'employee']
  //     }
  //   ]
  // },
  {
    title: 'Financeiro',
    roles: ['admin', 'director', 'ceo', 'diretor'],
    items: [
      {
        name: 'Projeções',
        href: '/projections',
        icon: TrendingUp,
        roles: ['admin']
      },
      {
        name: 'Clientes',
        href: '/clients',
        icon: Users,
        roles: ['admin']
      },
      {
        name: 'Treinamentos',
        href: '/training',
        icon: GraduationCap,
        roles: ['admin']
      },
      // {
      //   name: 'Painel Gerencial Financeiro',
      //   href: '/painel-gerencial',
      //   icon: BarChart3,
      //   roles: ['admin']
      // },
      // {
      //   name: 'RevFinOps',
      //   href: '/revfinops',
      //   icon: TrendingUp,
      //   roles: ['admin']
      // },
      {
        name: 'Big Numbers',
        href: '/financial/big-numbers',
        icon: DollarSign,
        roles: ['admin', 'ceo', 'diretor']
      },
      {
        name: 'Performance Clientes',
        href: '/financial/performance-clientes',
        icon: TrendingUp,
        roles: ['admin', 'ceo', 'diretor']
      },
      {
        name: 'DRE',
        href: '/financial/dre',
        icon: FileText,
        roles: ['admin', 'ceo']
      },
      {
        name: 'Matriz de Clientes',
        href: '/financial/client-matrix',
        icon: BarChart3,
        roles: ['admin', 'ceo']
      },
      {
        name: 'Matriz de BU',
        href: '/financial/bu-matrix',
        icon: BarChart3,
        roles: ['ceo']
      },
      {
        name: 'PROA',
        href: '/financial/proa',
        icon: Target,
        roles: ['ceo']
      },
      {
        name: 'Design System',
        href: '/design-system',
        icon: Palette,
        roles: ['admin']
      }
    ]
  }
]

export default function Sidebar({ isOpen, onClose, user, onJanesChatbotOpen }: SidebarProps) {
  const router = useRouterState()
  const pathname = router.location.pathname
  // Filter sections and items based on user role
  const filteredSections = navigationSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.roles.includes(user?.role || ''))
    }))
    .filter(section => 
      section.roles.includes(user?.role || '') && section.items.length > 0
    )

  return (
    <div
      className={`
        w-64 bg-brand-dark-light flex-shrink-0
        fixed inset-y-0 left-0 transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:inset-0 lg:block
      `}
      style={{ zIndex: Z_INDEX.SIDEBAR }}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-700">
          <div className="flex items-center">
            <h1 className="font-display text-lg sm:text-xl font-bold text-brand-primary">
              Monumenta
            </h1>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Manu Chatbot Button - Prominent at top */}
        <div className="px-4 pt-4 pb-2">
          <button
            onClick={onJanesChatbotOpen}
            className="flex items-center w-full px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <MessageCircle size={20} className="mr-3" />
            <span className="truncate font-semibold">🤖 Pergunta pra Manu</span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2 space-y-6 overflow-y-auto">
          {filteredSections.map((section, sectionIndex) => (
            <div key={section.title} className="space-y-2">
              {/* Section Header */}
              <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {section.title}
              </h3>
              
              {/* Section Items */}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon
                  const isActive = pathname === item.href
                  const isComingSoon = item.comingSoon
                  
                  // Render as disabled button if coming soon, otherwise as Link
                  if (isComingSoon) {
                    return (
                      <div
                        key={item.name}
                        className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                          opacity-50 cursor-not-allowed relative group
                          ${isActive 
                            ? 'bg-brand-primary/5 border-r-2 border-gray-600' 
                            : ''
                          }
                        `}
                        title="Em breve - Funcionalidade em desenvolvimento"
                        aria-label={`${item.name} - Em breve`}
                      >
                        <Icon size={18} className="mr-3 flex-shrink-0 text-gray-500" />
                        <span className="truncate text-gray-500">{item.name}</span>
                        <Clock 
                          size={12} 
                          className="ml-auto flex-shrink-0 text-amber-500" 
                          aria-hidden="true"
                        />
                        {/* Tooltip on hover */}
                        <div className="absolute left-full ml-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                          Em breve
                        </div>
                      </div>
                    )
                  }
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
                        ${isActive 
                          ? 'bg-brand-primary/10 text-brand-primary border-r-2 border-brand-primary' 
                          : 'text-gray-300 hover:bg-brand-dark hover:text-brand-primary'
                        }
                      `}
                      onClick={onClose}
                    >
                      <Icon size={18} className="mr-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Info */}
        <UserInfo user={user} />
      </div>
    </div>
  )
}